// src/component/UserAdminPage.js
import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useContext,
    useMemo,
  } from "react";
  import {
    collection,
    getDocs,
    query,
    doc,
    updateDoc,
    orderBy,
    startAt,
    endAt,
    serverTimestamp,
  } from "firebase/firestore";
  import { db } from "../api/firebase";
  import {
    ListItem,
    ListItemAvatar,
    Avatar,
    Typography,
    Box,
    TextField,
    InputAdornment,
    Card,
    CardContent,
    CircularProgress,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Switch,
    MenuItem,
    Select,
    FormControlLabel,
    Checkbox,
    Link,
    Grid,
    Divider,
    Stack,
    IconButton,
    Snackbar,
    Alert,
  } from "@mui/material";
  import Autocomplete, {
    createFilterOptions,
  } from "@mui/material/Autocomplete";
  import SearchIcon from "@mui/icons-material/Search";
  import DeleteIcon from "@mui/icons-material/Delete";
  import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
  import { useNavigate } from "react-router-dom";
  
  import PurposeSetting from "./PurposeSetting";
  import CourseSelection from "./CourseSelection";
  import ExportUsersButton from "./ExportUsersButton";
  import MMChatStatsClientPanel from "./MMChatStatsClientPanel";
  import { AppThemeContext } from "./AppShell";
  import { countries } from "../constants/countries";
  
  const prefectures = [
    "北海道",
    "青森県",
    "岩手県",
    "宮城県",
    "秋田県",
    "山形県",
    "福島県",
    "茨城県",
    "栃木県",
    "群馬県",
    "埼玉県",
    "千葉県",
    "東京都",
    "神奈川県",
    "新潟県",
    "富山県",
    "石川県",
    "福井県",
    "山梨県",
    "長野県",
    "岐阜県",
    "静岡県",
    "愛知県",
    "三重県",
    "滋賀県",
    "京都府",
    "大阪府",
    "兵庫県",
    "奈良県",
    "和歌山県",
    "鳥取県",
    "島根県",
    "岡山県",
    "広島県",
    "山口県",
    "徳島県",
    "香川県",
    "愛媛県",
    "高知県",
    "福岡県",
    "佐賀県",
    "長崎県",
    "熊本県",
    "大分県",
    "宮崎県",
    "鹿児島県",
    "沖縄県",
    "海外（アメリカ）",
    "海外（その他）",
  ];

  const countryFilterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => `${option.label} ${option.keywords || ""}`,
    trim: true,
  });

  // ISOコード(2文字)から国旗絵文字を生成
  const getCountryFlag = (keywords) => {
    if (!keywords) return "";
    const match = keywords.match(/\b([A-Z]{2})\b/);
    if (!match) return "";
    const code = match[1];
    return String.fromCodePoint(
      ...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
    );
  };

  const cityFilterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) =>
      typeof option === "string" ? option : option?.name || "",
    trim: true,
  });

  function UserAdminPage() {
    const [users, setUsers] = useState([]);
    const [unverifiedUsers, setUnverifiedUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [hideVerified, setHideVerified] = useState(false);
    const debounceTimeoutRef = useRef(null);
  
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
  
    const [openPrefectureDialog, setOpenPrefectureDialog] = useState(false);
    const [selectedPrefecture, setSelectedPrefecture] = useState("");
  
    const [openMunicipalityDialog, setOpenMunicipalityDialog] = useState(false);
    const [selectedMunicipality, setSelectedMunicipality] = useState(null);
    const [citiesByPrefecture, setCitiesByPrefecture] = useState({});
    const [loadingCities, setLoadingCities] = useState(false);
  
    const [openCountryDialog, setOpenCountryDialog] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);

    const [selectedPayment, setSelectedPayment] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
    const navigate = useNavigate();
    const theme = useContext(AppThemeContext) || {};
  
    const accent = theme.railActive || "#5A35FF";
    const surface = theme.surfaceBg || "#FFFFFF";
    const border = theme.bottomBorder || "rgba(0,0,0,0.12)";
    const textBase = theme.railText || "rgba(40,40,60,0.9)";
  
    // 🔁 入力のデバウンス
    useEffect(() => {
      debounceTimeoutRef.current = setTimeout(() => {
        setDebouncedSearchQuery(searchQuery.toLowerCase());
      }, 500);
  
      return () => {
        clearTimeout(debounceTimeoutRef.current);
      };
    }, [searchQuery]);
  
    // 🔁 検索語が変わったらユーザ取得
    useEffect(() => {
      if (debouncedSearchQuery) {
        fetchUsers();
      } else {
        setUsers([]);
        setUnverifiedUsers([]);
      }
    }, [debouncedSearchQuery]); // eslint-disable-line react-hooks/exhaustive-deps
  
    const fetchUsers = useCallback(async () => {
      setLoading(true);
      try {
        const usersQuery = query(
          collection(db, "users"),
          orderBy("name"),
          startAt(debouncedSearchQuery),
          endAt(debouncedSearchQuery + "\uf8ff")
        );
  
        const emailQuery = query(
          collection(db, "users"),
          orderBy("email"),
          startAt(debouncedSearchQuery),
          endAt(debouncedSearchQuery + "\uf8ff")
        );
  
        const [usersSnapshot, emailSnapshot] = await Promise.all([
          getDocs(usersQuery),
          getDocs(emailQuery),
        ]);
  
        const usersData = [
          ...usersSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })),
          ...emailSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })),
        ];
  
        const uniqueUsersData = Array.from(
          new Map(usersData.map((user) => [user.id, user])).values()
        );
  
        setUsers(uniqueUsersData);
        setUnverifiedUsers(uniqueUsersData.filter((user) => !user.isVerified));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
      setLoading(false);
    }, [debouncedSearchQuery]);
  
    const handleSearchChange = (e) => {
      setSearchQuery(e.target.value);
    };
  
    const handleHideVerifiedChange = (e) => {
      setHideVerified(e.target.checked);
    };
  
    // 2026 グループ活動：事務局が代理で参加意思を設定
    //  - 期日経過後でも事務局はここから「無法松」救済として書き込める
    //  - response = "join" | "decline" | null（null は未回答に戻す）
    const handleGroupActivity2026Set = async (userId, response) => {
      const userDocRef = doc(db, "users", userId);
      try {
        const nextValue =
          response === null
            ? null
            : {
                response,
                respondedAt: serverTimestamp(),
                setByAdmin: true,
              };
        await updateDoc(userDocRef, { groupActivity2026: nextValue });

        // ローカル状態の即時反映（respondedAt は ISO 文字列で代用してチップ表示用）
        const localValue =
          response === null
            ? null
            : {
                response,
                respondedAt: { toDate: () => new Date() },
                setByAdmin: true,
              };
        const patch = (u) =>
          u.id === userId ? { ...u, groupActivity2026: localValue } : u;
        setUsers((prev) => prev.map(patch));
        setUnverifiedUsers((prev) => prev.map(patch));
      } catch (err) {
        console.error("Error updating groupActivity2026:", err);
      }
    };

    const handleVerificationToggle = async (userId, isVerified) => {
      const userDocRef = doc(db, "users", userId);
      try {
        await updateDoc(userDocRef, {
          isVerified: !isVerified,
        });
  
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId ? { ...user, isVerified: !isVerified } : user
          )
        );
        setUnverifiedUsers((prevUsers) =>
          prevUsers.filter((user) => user.id !== userId)
        );
      } catch (error) {
        console.error("Error updating user verification:", error);
      }
    };
  
    const handleStopToggle = async (userId, isStop) => {
      const userDocRef = doc(db, "users", userId);
      try {
        await updateDoc(userDocRef, {
          isStop: !isStop,
        });
  
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId ? { ...user, isStop: !isStop } : user
          )
        );
      } catch (error) {
        console.error("Error updating user stop status:", error);
      }
    };
  
    const handleDuplicateToggle = async (userId, isDuplicate) => {
      const userDocRef = doc(db, "users", userId);
      try {
        await updateDoc(userDocRef, {
          isDuplicate: !isDuplicate,
        });
  
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId ? { ...user, isDuplicate: !isDuplicate } : user
          )
        );
      } catch (error) {
        console.error("Error updating user duplicate status:", error);
      }
    };
  
    const handleSekaimuraToggle = async (userId, isSekaimura) => {
      const userDocRef = doc(db, "users", userId);
      try {
        await updateDoc(userDocRef, {
          isSekaimura: !isSekaimura,
        });
  
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId ? { ...user, isSekaimura: !isSekaimura } : user
          )
        );
      } catch (error) {
        console.error("Error updating user Sekaimura status:", error);
      }
    };
  
    const handleEmailOptOutToggle = async (userId, currentValue) => {
      const userDocRef = doc(db, "users", userId);
      try {
        await updateDoc(userDocRef, {
          emailOptOut: !currentValue,
        });
  
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId ? { ...user, emailOptOut: !currentValue } : user
          )
        );
      } catch (error) {
        console.error("Error updating email opt-out:", error);
      }
    };
  
    const handleEditClick = (user) => {
      setSelectedUser(user);
      setOpenDialog(true);
    };
  
    const handleDialogClose = () => {
      setOpenDialog(false);
      setSelectedUser(null);
    };
  
    const handleDialogSave = async () => {
      if (!selectedUser) return;
      const userDocRef = doc(db, "users", selectedUser.id);
      try {
        await updateDoc(userDocRef, {
          registrationEmail: selectedUser.registrationEmail,
          registrationFurigana: selectedUser.registrationFurigana,
          registrationName: selectedUser.registrationName,
          registrationPhone: selectedUser.registrationPhone,
        });
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === selectedUser.id ? { ...selectedUser } : user
          )
        );
        handleDialogClose();
      } catch (error) {
        console.error("Error updating user info:", error);
      }
    };
  
    const handleInputChange = (e) => {
      const { name, value } = e.target;
      setSelectedUser((prevUser) => ({ ...prevUser, [name]: value }));
    };
  
    const handlePrefectureClick = (user) => {
      setSelectedUser(user);
      setSelectedPrefecture(user.prefecture || "");
      setOpenPrefectureDialog(true);
    };
  
    const handlePrefectureDialogClose = () => {
      setOpenPrefectureDialog(false);
      setSelectedUser(null);
    };
  
    const handlePrefectureSave = async () => {
      if (!selectedUser) return;
      const userDocRef = doc(db, "users", selectedUser.id);
      try {
        await updateDoc(userDocRef, {
          prefecture: selectedPrefecture,
        });
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === selectedUser.id
              ? { ...selectedUser, prefecture: selectedPrefecture }
              : user
          )
        );
        handlePrefectureDialogClose();
      } catch (error) {
        console.error("Error updating user prefecture:", error);
      }
    };
  
    const cityOptions = useMemo(() => {
      if (!selectedUser?.prefecture) return [];
      return citiesByPrefecture[selectedUser.prefecture] || [];
    }, [selectedUser?.prefecture, citiesByPrefecture]);

    const handleMunicipalityClick = async (user) => {
      setSelectedUser(user);
      setOpenMunicipalityDialog(true);

      // Load cities if not already loaded
      if (Object.keys(citiesByPrefecture).length === 0) {
        setLoadingCities(true);
        try {
          const res = await fetch("/data/japanCities.json", { cache: "force-cache" });
          if (res.ok) {
            const json = await res.json();
            setCitiesByPrefecture(json || {});
            // Set initial selection from loaded data
            const options = json[user.prefecture] || [];
            const match = options.find((c) =>
              typeof c === "string" ? c === user.city : c?.name === user.city
            );
            setSelectedMunicipality(match || null);
          }
        } catch (err) {
          console.error("load japanCities.json error:", err);
        } finally {
          setLoadingCities(false);
        }
      } else {
        // Already loaded, find current city in options
        const options = citiesByPrefecture[user.prefecture] || [];
        const match = options.find((c) =>
          typeof c === "string" ? c === user.city : c?.name === user.city
        );
        setSelectedMunicipality(match || null);
      }
    };
  
    const handleMunicipalityDialogClose = () => {
      setOpenMunicipalityDialog(false);
      setSelectedUser(null);
    };
  
    const handleMunicipalitySave = async () => {
      if (!selectedUser || !selectedMunicipality) return;
      const userDocRef = doc(db, "users", selectedUser.id);
      const pref = selectedUser.prefecture;
      const cityName =
        typeof selectedMunicipality === "string"
          ? selectedMunicipality
          : selectedMunicipality?.name || "";
      const cityLat =
        typeof selectedMunicipality === "object" &&
        Number.isFinite(selectedMunicipality?.lat)
          ? selectedMunicipality.lat
          : null;
      const cityLng =
        typeof selectedMunicipality === "object" &&
        Number.isFinite(selectedMunicipality?.lng)
          ? selectedMunicipality.lng
          : null;
      const cityLocKey =
        typeof selectedMunicipality === "object" && selectedMunicipality?.locationKey
          ? selectedMunicipality.locationKey
          : `JP:${pref}:${cityName}`;

      try {
        await updateDoc(userDocRef, {
          city: cityName,
          lat: cityLat,
          lng: cityLng,
          locationKey: cityLocKey,
          geoSource: "japanCities.json",
          locationLabel: `${pref} ${cityName}`,
        });
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === selectedUser.id
              ? { ...selectedUser, city: cityName }
              : user
          )
        );
        handleMunicipalityDialogClose();
        setSnackbar({ open: true, message: "市区町村を更新しました", severity: "success" });
      } catch (error) {
        console.error("Error updating user municipality:", error);
        setSnackbar({ open: true, message: "更新に失敗しました", severity: "error" });
      }
    };
  
    const handleCountryClick = (user) => {
      setSelectedUser(user);
      const match = countries.find((c) => c.label === user.country) || null;
      setSelectedCountry(match);
      setOpenCountryDialog(true);
    };

    const handleCountryDialogClose = () => {
      setOpenCountryDialog(false);
      setSelectedUser(null);
      setSelectedCountry(null);
    };

    const handleCountrySave = async () => {
      if (!selectedUser || !selectedCountry) return;
      const userDocRef = doc(db, "users", selectedUser.id);
      try {
        await updateDoc(userDocRef, {
          country: selectedCountry.label,
          lat: selectedCountry.lat ?? null,
          lng: selectedCountry.lng ?? null,
        });
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === selectedUser.id
              ? { ...selectedUser, country: selectedCountry.label }
              : user
          )
        );
        handleCountryDialogClose();
        setSnackbar({ open: true, message: "国名を更新しました", severity: "success" });
      } catch (error) {
        console.error("Error updating user country:", error);
        setSnackbar({ open: true, message: "更新に失敗しました", severity: "error" });
      }
    };

    const handleGroupClick = (groupId) => {
      window.open(`/group/${groupId}`, "_blank");
    };
  
    const handleGroupClick2025h = (groupId) => {
      window.open(`/groups2025/${groupId}`, "_blank");
    };
  
    const handleGroupClick2025hl = (groupId) => {
      window.open(`/groups2025hl/${groupId}`, "_blank");
    };
  
    const displayedUsers = hideVerified ? unverifiedUsers : users;
  
    const handleOpenDeleteDialog = (userId, paymentIndex) => {
      setSelectedPayment({ userId, paymentIndex });
      setOpenDeleteDialog(true);
    };
  
    const handleCloseDeleteDialog = () => {
      setOpenDeleteDialog(false);
      setSelectedPayment(null);
    };
  
    const handleDeletePayment = async () => {
      if (!selectedPayment) return;
  
      const { userId, paymentIndex } = selectedPayment;
      const userDocRef = doc(db, "users", userId);
  
      try {
        const user = users.find((user) => user.id === userId);
        if (!user) throw new Error("User not found");
  
        const updatedPayments = [...(user.payments || [])];
        updatedPayments.splice(paymentIndex, 1);
  
        await updateDoc(userDocRef, { payments: updatedPayments });
  
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId ? { ...user, payments: updatedPayments } : user
          )
        );
  
        handleCloseDeleteDialog();
      } catch (error) {
        console.error("Error deleting payment:", error);
      }
    };
  
    return (
      <Box
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          pb: { xs: 3, sm: 4 },
        }}
      >
        {/* ヘッダー行 */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: "0.18em",
                fontSize: 11,
                opacity: 0.8,
                color: textBase,
              }}
            >
              ADMIN / USERS
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.03em",
                color: textBase,
              }}
            >
              ユーザー管理
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}
            >
              ユーザー情報の検索・ステータス管理・支払い履歴の確認と修正を行います。
            </Typography>
          </Box>
  
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate("/admin")}
            >
              管理者メニューに戻る
            </Button>
          </Stack>
        </Box>
  
        <Divider sx={{ mb: 2.5 }} />
  
        {/* 上部ツール群 */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <ExportUsersButton />
            {/* 必要に応じて他のボタンもここへ */}
          </Stack>
  
        </Stack>
  
        {/* 検索 + 絞り込み */}
        <TextField
          placeholder="名前・メールで検索"
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
  
        <Box
          sx={{
            mt: 1,
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 1.5,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={hideVerified}
                onChange={handleHideVerifiedChange}
                color="primary"
              />
            }
            label="認証済みのユーザーを省く"
          />
          <Typography
            variant="body2"
            sx={{ ml: { sm: 1.5 }, color: "rgba(0,0,0,0.7)" }}
          >
            該当ユーザー数:{" "}
            <strong>{displayedUsers.length.toLocaleString()}</strong>
          </Typography>
        </Box>
  
        {/* 検索前メッセージ */}
        {searchQuery === "" ? (
          <Typography variant="body1" align="center" sx={{ mt: 4 }}>
            検索ボックスに名前またはメールアドレスを入力してユーザーを検索してください。
          </Typography>
        ) : loading ? (
          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body1">ユーザー情報を取得中です…</Typography>
          </Box>
        ) : displayedUsers.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ mt: 4 }}>
            該当するユーザーはいません。
          </Typography>
        ) : (
          // ▼ 検索結果：カードグリッド
          <Grid container spacing={2.5} sx={{ mt: 1 }}>
            {displayedUsers.map((user) => (
              <Grid item key={user.id} xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${border}`,
                    bgcolor: surface,
                    boxShadow: "0 10px 26px rgba(0,0,0,0.06)",
                    transition: "transform .15s ease-out, box-shadow .15s ease-out",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.2 }}>
                    <Stack spacing={1.8}>
                      {/* 上段：アバター + 基本情報 */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.8,
                          alignItems: "center",
                        }}
                      >
                        {/* 左：アバタータイル */}
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 3,
                            background:
                              "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                              `linear-gradient(135deg, ${accent}22, ${accent}11)`,
                            boxShadow:
                              "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <Avatar
                            src={user.avatar}
                            alt={user.name || "user"}
                            sx={{
                              width: 46,
                              height: 46,
                              border: "2px solid rgba(255,255,255,0.85)",
                            }}
                          />
                        </Box>
  
                        {/* 右：名前・メールなど */}
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              color: textBase,
                              mb: 0.3,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {user.name || "名前未設定"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: 13,
                              color: "rgba(0,0,0,0.7)",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {user.email || "メールアドレス未設定"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "rgba(0,0,0,0.6)", mt: 0.5, display: "block" }}
                          >
                            UID: {user.id}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "rgba(0,0,0,0.6)", display: "block" }}
                          >
                            登録日時:{" "}
                            {user.createdAt?.toDate().toLocaleString("ja-JP") ||
                              "未設定"}
                          </Typography>
                        </Box>
                      </Box>
  
                      {/* スイッチ群 */}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="caption" sx={{ mr: 0.5 }}>
                            認証
                          </Typography>
                          <Switch
                            size="small"
                            checked={user.isVerified === true}
                            onChange={() =>
                              handleVerificationToggle(user.id, user.isVerified)
                            }
                          />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="caption" sx={{ mr: 0.5 }}>
                            停止
                          </Typography>
                          <Switch
                            size="small"
                            checked={user.isStop === true}
                            onChange={() => handleStopToggle(user.id, user.isStop)}
                          />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="caption" sx={{ mr: 0.5 }}>
                            重複登録
                          </Typography>
                          <Switch
                            size="small"
                            checked={user.isDuplicate === true}
                            onChange={() =>
                              handleDuplicateToggle(user.id, user.isDuplicate)
                            }
                          />
                        </Box>
                        {/* 非表示化：セカイムラ権限の手動付与トグル
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="caption" sx={{ mr: 0.5 }}>
                            セカイムラ民
                          </Typography>
                          <Switch
                            size="small"
                            checked={user.isSekaimura === true}
                            onChange={() =>
                              handleSekaimuraToggle(user.id, user.isSekaimura)
                            }
                          />
                        </Box>
                        */}
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography variant="caption" sx={{ mr: 0.5 }}>
                            メール不要
                          </Typography>
                          <Switch
                            size="small"
                            checked={user.emailOptOut === true}
                            onChange={() =>
                              handleEmailOptOutToggle(
                                user.id,
                                user.emailOptOut === true
                              )
                            }
                          />
                        </Box>
                      </Box>

                      {/* 2026 グループ参加意思（事務局による代理入力 = 無法松救済） */}
                      {(() => {
                        const ga = user.groupActivity2026;
                        const current = ga?.response || null;
                        const respondedAtLabel = (() => {
                          const v = ga?.respondedAt;
                          if (!v) return null;
                          try {
                            const d = typeof v.toDate === "function" ? v.toDate() : new Date(v);
                            if (Number.isNaN(d.getTime())) return null;
                            return d.toLocaleString("ja-JP");
                          } catch {
                            return null;
                          }
                        })();
                        const pillSx = (active, tone) => ({
                          minWidth: 0,
                          px: 1.2,
                          py: 0.3,
                          borderRadius: 999,
                          textTransform: "none",
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: 1.4,
                          ...(active && tone === "join"
                            ? { bgcolor: "#2e7d32", "&:hover": { bgcolor: "#256528" }, color: "#fff" }
                            : {}),
                          ...(active && tone === "decline"
                            ? { bgcolor: "#616161", "&:hover": { bgcolor: "#4d4d4d" }, color: "#fff" }
                            : {}),
                          ...(active && tone === "none"
                            ? { bgcolor: "#9e9e9e", "&:hover": { bgcolor: "#7e7e7e" }, color: "#fff" }
                            : {}),
                        });
                        return (
                          <Box
                            sx={{
                              p: 1.2,
                              borderRadius: 1.5,
                              border: "1px dashed rgba(0,0,0,0.18)",
                              bgcolor: "rgba(0,0,0,0.02)",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                display: "block",
                                mb: 0.5,
                                color: "rgba(0,0,0,0.7)",
                              }}
                            >
                              2026グループ参加意思（事務局代理入力／無法松救済）
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              <Button
                                size="small"
                                variant={current === "join" ? "contained" : "outlined"}
                                onClick={() => handleGroupActivity2026Set(user.id, "join")}
                                sx={pillSx(current === "join", "join")}
                              >
                                参加する
                              </Button>
                              <Button
                                size="small"
                                variant={current === "decline" ? "contained" : "outlined"}
                                onClick={() => handleGroupActivity2026Set(user.id, "decline")}
                                sx={pillSx(current === "decline", "decline")}
                              >
                                参加しない
                              </Button>
                              <Button
                                size="small"
                                variant={current === null ? "contained" : "outlined"}
                                onClick={() => handleGroupActivity2026Set(user.id, null)}
                                sx={pillSx(current === null, "none")}
                              >
                                未回答に戻す
                              </Button>
                            </Stack>
                            {(respondedAtLabel || ga?.setByAdmin) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 0.75,
                                  color: "rgba(0,0,0,0.55)",
                                }}
                              >
                                {respondedAtLabel && `最終更新: ${respondedAtLabel}`}
                                {ga?.setByAdmin && "（事務局による入力）"}
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}

                      {/* 支払い情報 */}
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          Payments
                        </Typography>
                        {user.payments && user.payments.length > 0 ? (
                          user.payments.map((payment, index) => (
                            <Box
                              key={index}
                              sx={{
                                p: 1,
                                mb: 0.7,
                                borderRadius: 2,
                                border: `1px solid ${border}`,
                                bgcolor: "rgba(255,255,255,0.8)",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ fontSize: 12 }}>
                                <Typography variant="body2" sx={{ fontSize: 12 }}>
                                  <strong>フェーズ:</strong>{" "}
                                  {payment.paymentPhase || "未設定"}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: 12 }}>
                                  <strong>方法:</strong>{" "}
                                  {payment.paymentMethod || "未設定"}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: 12 }}>
                                  <strong>金額:</strong>{" "}
                                  {payment.amountTotal || "未設定"}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: 12 }}>
                                  <strong>タイムスタンプ:</strong>{" "}
                                  {payment.timestamp
                                    ?.toDate?.()
                                    ?.toLocaleString("ja-JP") || "未設定"}
                                </Typography>
                              </Box>
                              <IconButton
                                color="secondary"
                                size="small"
                                onClick={() =>
                                  handleOpenDeleteDialog(user.id, index)
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: 12 }}>
                            支払い情報がありません。
                          </Typography>
                        )}
                      </Box>
  
                      {/* 所在・登録情報 */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                          gap: 0.6,
                          fontSize: 12,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          <strong>目的:</strong>{" "}
                          {user.purpose || "未設定"}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          <strong>居住県:</strong>{" "}
                          {user.prefecture || "未設定"}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          <strong>市区町村:</strong>{" "}
                          {user.city || user.municipality || "未設定"}
                        </Typography>
                        {user.prefecture?.includes("海外") && (
                          <Typography variant="body2" sx={{ fontSize: 12 }}>
                            <strong>国名:</strong>{" "}
                            {user.country
                              ? `${getCountryFlag(countries.find((c) => c.label === user.country)?.keywords)} ${user.country}`
                              : "未設定"}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          <strong>登録名:</strong>{" "}
                          {user.registrationName || "未設定"}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          <strong>登録電話番号:</strong>{" "}
                          {user.registrationPhone || "未設定"}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          <strong>登録ふりがな:</strong>{" "}
                          {user.registrationFurigana || "未設定"}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          <strong>登録メールアドレス:</strong>{" "}
                          {user.registrationEmail || "未設定"}
                        </Typography>
                      </Box>
  
                      {/* グループリンク */}
                      <Box sx={{ fontSize: 12 }}>
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          <strong>参加グループID:</strong>{" "}
                          {user.participatingGroup ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() =>
                                handleGroupClick(user.participatingGroup)
                              }
                            >
                              {user.participatingGroup}
                            </Link>
                          ) : (
                            "なし"
                          )}
                        </Typography>
  
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          <strong>参加グループ2025ID:</strong>{" "}
                          {user.groups2025 ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() =>
                                handleGroupClick2025h(user.groups2025)
                              }
                            >
                              {user.groups2025}
                            </Link>
                          ) : (
                            "なし"
                          )}
                        </Typography>
  
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          <strong>参加グループ2025八重の誓い:</strong>{" "}
                          {user.groups2025SecondHalf ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() =>
                                handleGroupClick2025hl(user.groups2025SecondHalf)
                              }
                            >
                              {user.groups2025SecondHalf}
                            </Link>
                          ) : (
                            "なし"
                          )}
                        </Typography>
                      </Box>
  
                      {/* 操作用ボタン */}
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        sx={{ mt: 0.5 }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleEditClick(user)}
                        >
                          申し込みデータを修正
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handlePrefectureClick(user)}
                        >
                          居住県を修正
                        </Button>
                        {user.prefecture && !["海外（アメリカ）", "海外（その他）"].includes(user.prefecture) && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleMunicipalityClick(user)}
                          >
                            市区町村を修正
                          </Button>
                        )}
                        {user.prefecture?.includes("海外") && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleCountryClick(user)}
                          >
                            国名を修正
                          </Button>
                        )}
                      </Stack>
  
                      {/* 目的 / コース設定 */}
                      {/* <Box sx={{ mt: 0.5 }}>
                        <PurposeSetting userId={user.id} />
                        <CourseSelection userId={user.id} isAdmin />
                      </Box> */}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
  
        {/* ▼ ユーザー編集ダイアログ */}
        <Dialog open={openDialog} onClose={handleDialogClose} fullWidth>
          <DialogTitle>ユーザー情報を編集</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="登録メールアドレス"
              type="email"
              fullWidth
              name="registrationEmail"
              value={selectedUser?.registrationEmail || ""}
              onChange={handleInputChange}
            />
            <TextField
              margin="dense"
              label="登録ふりがな"
              type="text"
              fullWidth
              name="registrationFurigana"
              value={selectedUser?.registrationFurigana || ""}
              onChange={handleInputChange}
            />
            <TextField
              margin="dense"
              label="登録名"
              type="text"
              fullWidth
              name="registrationName"
              value={selectedUser?.registrationName || ""}
              onChange={handleInputChange}
            />
            <TextField
              margin="dense"
              label="登録電話番号"
              type="text"
              fullWidth
              name="registrationPhone"
              value={selectedUser?.registrationPhone || ""}
              onChange={handleInputChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>キャンセル</Button>
            <Button onClick={handleDialogSave}>保存</Button>
          </DialogActions>
        </Dialog>
  
        {/* ▼ 都道府県ダイアログ */}
        <Dialog
          open={openPrefectureDialog}
          onClose={handlePrefectureDialogClose}
          fullWidth
        >
          <DialogTitle>居住県を修正する</DialogTitle>
          <DialogContent>
            <Select
              fullWidth
              value={selectedPrefecture}
              onChange={(e) => setSelectedPrefecture(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                都道府県を選択
              </MenuItem>
              {prefectures.map((pref) => (
                <MenuItem key={pref} value={pref}>
                  {pref}
                </MenuItem>
              ))}
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePrefectureDialogClose}>キャンセル</Button>
            <Button onClick={handlePrefectureSave}>保存</Button>
          </DialogActions>
        </Dialog>
  
        {/* ▼ 市区町村ダイアログ（全都道府県対応） */}
        <Dialog
          open={openMunicipalityDialog}
          onClose={handleMunicipalityDialogClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>市区町村を修正する</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="都道府県"
              value={selectedUser?.prefecture || ""}
              disabled
              sx={{ mb: 2, mt: 1 }}
            />
            <Autocomplete
              fullWidth
              options={cityOptions}
              value={selectedMunicipality}
              onChange={(_, newValue) => setSelectedMunicipality(newValue)}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.name || ""
              }
              isOptionEqualToValue={(option, value) => {
                const optName = typeof option === "string" ? option : option?.name;
                const valName = typeof value === "string" ? value : value?.name;
                return optName === valName;
              }}
              filterOptions={cityFilterOptions}
              autoHighlight
              clearOnBlur={false}
              loading={loadingCities}
              noOptionsText={
                loadingCities
                  ? "読み込み中です…"
                  : "該当する市区町村が見つかりません"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="市区町村"
                  placeholder="市区町村名を入力して検索"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCities ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleMunicipalityDialogClose}>キャンセル</Button>
            <Button onClick={handleMunicipalitySave} disabled={!selectedMunicipality}>
              保存
            </Button>
          </DialogActions>
        </Dialog>
  
        {/* ▼ 国名ダイアログ（海外ユーザー用） */}
        <Dialog
          open={openCountryDialog}
          onClose={handleCountryDialogClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>国名を修正する</DialogTitle>
          <DialogContent>
            <Autocomplete
              fullWidth
              options={countries}
              value={selectedCountry}
              onChange={(_, newValue) => setSelectedCountry(newValue)}
              groupBy={(option) => option.region}
              getOptionLabel={(option) =>
                `${getCountryFlag(option.keywords)} ${option.label}`
              }
              isOptionEqualToValue={(option, value) =>
                option.label === value.label
              }
              filterOptions={countryFilterOptions}
              autoHighlight
              clearOnBlur={false}
              renderOption={(props, option) => (
                <li {...props}>
                  <span style={{ marginRight: 8, fontSize: 20 }}>
                    {getCountryFlag(option.keywords)}
                  </span>
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="国名"
                  placeholder="国名を入力して検索"
                  sx={{ mt: 1 }}
                />
              )}
              renderGroup={(params) => (
                <li key={params.key}>
                  <Typography
                    sx={{
                      px: 2,
                      py: 0.5,
                      fontSize: 12,
                      fontWeight: 700,
                      color: accent,
                      bgcolor: "rgba(90,53,255,0.06)",
                    }}
                  >
                    {params.group}
                  </Typography>
                  <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                </li>
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCountryDialogClose}>キャンセル</Button>
            <Button onClick={handleCountrySave} disabled={!selectedCountry}>
              保存
            </Button>
          </DialogActions>
        </Dialog>

        {/* ▼ 支払い削除ダイアログ */}
        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
          <DialogTitle>支払い情報を削除</DialogTitle>
          <DialogContent>
            <Typography>
              この支払い情報を削除してもよろしいですか？
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>いいえ</Button>
            <Button onClick={handleDeletePayment}>はい</Button>
          </DialogActions>
        </Dialog>

        {/* ▼ Snackbar通知 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
  
  export default UserAdminPage;

  